import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["toDate", "fromDate", "value", "range", "name", "acceptButton", "cancelButton", "numberOfMonths", "error", "locale", "localization", "onChange"];
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { convertedDateToObject } from '@helpers';
import { Icon } from '@elements/icon';
import { IconButton } from '@elements/icon-button';
import { colors } from '@themes';
import { StyledDayPickerButtonsWrapper, StyledPopover } from './day-picker-field.styles';
import Calendar from '@images/calendar.svg';
import { DayPicker, DayPickerButtons, RangeButtons } from './components';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

/**
 * Date Picker Range component.
 *
 * @author Mikhail Siutsou
 * @function DatePickerRange
 * @category components
 * @param {DatePickerRangeProps} params day picker range params.
 * @return {JSX.Element} DatePicker component.
 */
var DayPickerRange = function DayPickerRange(_ref) {
  var toDate = _ref.toDate,
      fromDate = _ref.fromDate,
      value = _ref.value,
      range = _ref.range,
      name = _ref.name,
      acceptButton = _ref.acceptButton,
      cancelButton = _ref.cancelButton,
      _ref$numberOfMonths = _ref.numberOfMonths,
      numberOfMonths = _ref$numberOfMonths === void 0 ? 1 : _ref$numberOfMonths,
      error = _ref.error,
      locale = _ref.locale,
      localization = _ref.localization,
      onChange = _ref.onChange,
      textfieldProps = _objectWithoutProperties(_ref, _excluded);

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var initialRange = {
    to: convertedDateToObject(value === null || value === void 0 ? void 0 : value.to),
    from: convertedDateToObject(value === null || value === void 0 ? void 0 : value.from)
  };

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      anchorEl = _useState2[0],
      setAnchorEl = _useState2[1];

  var _useState3 = useState(initialRange),
      _useState4 = _slicedToArray(_useState3, 2),
      selectedRange = _useState4[0],
      setSelectedRange = _useState4[1];

  var open = Boolean(anchorEl);
  var id = open ? "daypicker-".concat(name || 'field') : undefined;
  /**
   * Click handler.
   *
   * @author Mikhail Siutsou
   * @param {React.MouseEvent<HTMLInputElement>} event event.
   * @return {void}
   */

  var handleClick = function handleClick(event) {
    setAnchorEl(event.currentTarget);
  };
  /**
   * Close handler.
   *
   * @author Mikhail Siutsou
   * @return {void}
   */


  var handleClose = useCallback(function () {
    setAnchorEl(null);

    if (onChange) {
      var newValues = {
        target: {
          value: undefined
        }
      };
      onChange(newValues);
    }

    setSelectedRange({
      to: convertedDateToObject(value === null || value === void 0 ? void 0 : value.to),
      from: convertedDateToObject(value === null || value === void 0 ? void 0 : value.from)
    }); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value === null || value === void 0 ? void 0 : value.from, value === null || value === void 0 ? void 0 : value.to]);
  /**
   * Click handler.
   *
   * @author Mikhail Siutsou
   * @param {SelectRangeEventHandler} params range select.
   * @return {void}
   */

  var handleRangeSelect = useCallback(function (range) {
    setSelectedRange({
      to: convertedDateToObject(range === null || range === void 0 ? void 0 : range.to),
      from: convertedDateToObject(range === null || range === void 0 ? void 0 : range.from)
    });
  }, []); // /**
  //  * Get formatted value.
  //  *
  //  * @author Mikhail Siutsou
  //  * @param {DateRange | undefined} value range value.
  //  * @return {string}
  //  */
  // const getFormattedValue = (value: DateRange | undefined): string => {
  //   const formattedValues = ['XX.XX.XX', 'XX.XX.XX'];
  //
  //   if (!value || (!value.from && !value.to)) {
  //     return '';
  //   }
  //
  //   if (value.from) {
  //     formattedValues[0] = format(value.from, 'dd.MM.yyyy');
  //   }
  //
  //   if (value.to) {
  //     formattedValues[1] = format(value.to, 'dd.MM.yyyy');
  //   }
  //
  //   if (!value.to) {
  //     return formattedValues[0];
  //   }
  //
  //   return formattedValues.join(' - ');
  // };

  /**
   * Change date handler.
   *
   * @author Mikhail Siutsou
   * @return {void}
   */

  var handleChangeDate = useCallback(function () {
    if (onChange) {
      var newValues = {
        target: {
          value: {
            to: selectedRange === null || selectedRange === void 0 ? void 0 : selectedRange.to,
            from: selectedRange === null || selectedRange === void 0 ? void 0 : selectedRange.from
          }
        }
      };
      onChange(newValues);
    }

    setAnchorEl(null);
    setSelectedRange({
      to: selectedRange === null || selectedRange === void 0 ? void 0 : selectedRange.to,
      from: selectedRange === null || selectedRange === void 0 ? void 0 : selectedRange.from
    });
  }, [onChange, selectedRange === null || selectedRange === void 0 ? void 0 : selectedRange.from, selectedRange === null || selectedRange === void 0 ? void 0 : selectedRange.to]);
  /**
   * Change date handler by bytton.
   *
   * @author Mikhail Siutsou
   * @param {{to: RangeButtonProps['to'];from: RangeButtonProps['from'];}} range value.
   * @return {void}
   */

  var handleButtonRange = useCallback(function (range) {
    return function () {
      setSelectedRange({
        to: convertedDateToObject(range.to),
        from: convertedDateToObject(range.from)
      });
    };
  }, []); // /**
  //  * Clear date value by bytton.
  //  *
  //  * @author Mikhail Siutsou
  //  * @return {void}
  //  */
  // const handleClearButton = useCallback(
  //   (event: MouseEvent<HTMLButtonElement>): void => {
  //     event.stopPropagation();
  //     event.preventDefault();
  //     if (onChange) {
  //       const newValues = {
  //         target: {
  //           value: {
  //             to:   undefined,
  //             from: undefined,
  //           },
  //         },
  //       } as unknown as ChangeEvent<HTMLInputElement>;
  //
  //       onChange(newValues);
  //       setSelectedRange({
  //         to:   undefined,
  //         from: undefined,
  //       });
  //     }
  //   },
  //   [onChange],
  // );
  //
  // const formattedValue = getFormattedValue(value);
  //
  // const textError =
  //   typeof error === 'string' ? error : error && (error?.from || error?.to);

  return /*#__PURE__*/_jsxs(Box, {
    children: [/*#__PURE__*/_jsx(IconButton, {
      sx: {
        marginTop: '3px',
        '& i': {
          '& svg': {
            '& path': {
              fill: selectedRange !== null && selectedRange !== void 0 && selectedRange.from || selectedRange !== null && selectedRange !== void 0 && selectedRange.to && selectedRange !== null && selectedRange !== void 0 && selectedRange.from ? colors.internationalOrange : 'rgb(158, 158, 158)'
            }
          }
        },
        '&:hover': {
          '& i': {
            '& svg': {
              '& path': {
                fill: selectedRange !== null && selectedRange !== void 0 && selectedRange.from || selectedRange !== null && selectedRange !== void 0 && selectedRange.to && selectedRange !== null && selectedRange !== void 0 && selectedRange.from ? colors.internationalOrange : colors.white
              }
            }
          }
        }
      },
      onClick: handleClick,
      children: /*#__PURE__*/_jsx(Icon, {
        src: Calendar,
        width: 28,
        height: 28
      })
    }), /*#__PURE__*/_jsx(Box, {
      position: "relative",
      children: /*#__PURE__*/_jsx(StyledPopover, {
        id: id,
        open: open,
        anchorEl: anchorEl,
        onClose: handleClose,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left'
        },
        PaperProps: {
          elevation: 0
        },
        children: /*#__PURE__*/_jsxs(Box, {
          children: [range && /*#__PURE__*/_jsx(RangeButtons, {
            range: range,
            selectedRange: selectedRange,
            handleButtonRange: handleButtonRange
          }), /*#__PURE__*/_jsx(DayPicker, {
            fromDate: fromDate,
            toDate: toDate,
            numberOfMonths: numberOfMonths,
            mode: "range",
            selected: selectedRange,
            onSelect: handleRangeSelect,
            locale: locale,
            localization: localization,
            setSelectedDate: setSelectedRange,
            modifiersClassNames: {
              today: 'today'
            },
            showOutsideDays: true,
            fixedWeeks: true
          }), /*#__PURE__*/_jsx(StyledDayPickerButtonsWrapper, {
            children: /*#__PURE__*/_jsx(DayPickerButtons, {
              numberOfMonths: numberOfMonths,
              handleClose: handleClose,
              handleChangeDate: handleChangeDate,
              acceptButton: t('common.datePicker.chooseDates'),
              cancelButton: t('common.datePicker.cancel')
            })
          })]
        })
      })
    })]
  });
};

export { DayPickerRange };